import { call, put, takeEvery, fork, select, cancel } from 'redux-saga/effects';
import { push } from 'react-router-redux';
import {
  types,
  loginSuccess,
  updateUserInfo,
  updatePermission,
  resetPasswordResponse,
  logout,
  cleanUserCredentials,
  requestForResetPasswordByEmailSuccess,
  requestForResetPasswordByEmailFailure,
  changePasswordByTokenSuccess,
  changePasswordByTokenFailure,
} from '@actions/authentication';
import { selectUserId, tokenSelector, isAttendee } from '@selectors/authentication';
import {
  getUserInfo,
  getEditUser,
  resetUserPassword,
  requestForResetUserPasswordByEmail,
  removeCredentials,
  setCredentials,
  postLoginToAPI,
  serverLogout,
  requestNewOTP,
} from '@services/authenticationResource';
import { displaySnackBar } from '@actions/snackbar';
import { ROLES } from '@config/authentication';
import { getCountriesListSaga } from './countries';
import { getAdminSettingsSaga } from './admin';

export function* authenticationLoginSaga(action) {
  try {
    const res = yield call(postLoginToAPI, action);
    yield put(loginSuccess(res.data.userId, res.data.id));
    setCredentials(res.data.userId, res.data.id);
    yield put(push('/'));
    yield call(getInfoSaga);
  } catch (err) {
    yield put({ type: types.LOGIN.FAILURE });
  }
}

export function* authenticationLogoutSaga(action) {
  const token = yield select(tokenSelector);
  const isUserHasAttendeeRole = yield select(isAttendee);
  if (!isUserHasAttendeeRole) yield call(serverLogout, token);
  removeCredentials();
  yield put(cleanUserCredentials());
  yield (document.location.href = '/login');
}

export function* getInfoSaga(action) {
  try {
    const userId = yield select(selectUserId);
    // Getting global info
    yield call(getCountriesListSaga);

    if (!userId) {
      yield put(updatePermission(null));
      return false;
    }
    const token = yield select(tokenSelector);
    // Getting user info
    const { data: userInfo } = yield call(getUserInfo, token, userId);
    yield put(updateUserInfo(userInfo));
    const { data: roles } = yield call(getEditUser, token, userId);
    const role = roles.length ? roles[0].name : null;
    yield put(updatePermission(role));
    if (role === ROLES.SUPER_ADMIN) yield call(getAdminSettingsSaga);
  } catch (err) {
    if (err.response.status === 401) {
      yield put(cleanUserCredentials());
      yield put(updatePermission(null));
    }
    yield put(displaySnackBar(false, err.message || 'Error when fetching user info.'));
  }
}

export function* resetUserPasswordSaga(action) {
  const token = yield select(tokenSelector);
  const currentUserId = yield select(selectUserId);
  const userId = action.userId || currentUserId;
  try {
    yield call(resetUserPassword, token, userId, action.newPassword);
    yield put(resetPasswordResponse(false));
    if (userId === currentUserId) {
      yield put(logout());
    }
  } catch (err) {
    yield put(resetPasswordResponse(true));
  }
}

export function* resetUserPasswordSnackbarSaga(action) {
  if (action.error) {
    yield put(displaySnackBar(false, 'Error while reseting the password.'));
  } else {
    yield put(displaySnackBar(true, 'Successfully reset the password.'));
  }
}

export function* requestForResetPasswordByEmailSaga(action) {
  try {
    yield call(requestForResetUserPasswordByEmail, action.email);
    yield put(requestForResetPasswordByEmailSuccess());
  } catch (err) {
    yield put(requestForResetPasswordByEmailFailure());
  }
}

export function* changePasswordByTokenSaga(action) {
  try {
    yield call(resetUserPassword, action.token, action.userId, action.newPassword);
    yield put(changePasswordByTokenSuccess());
  } catch (err) {
    yield put(changePasswordByTokenFailure());
  }
}

export function* loginWithTokenSaga(action) {
  try {
    yield put(loginSuccess(action.userId, action.token));
    setCredentials(action.userId, action.token);
    yield call(getInfoSaga);
    yield put(push('/transcripts'));
    yield put(displaySnackBar(true, 'Welcome to transcripts dashboard.'));
  } catch (err) {
    yield put(displaySnackBar(false, err.message || 'Error when logging in.'));
  }
}

export function* setUserTokenSaga(action) {
  try {
    yield put(loginSuccess(action.userId, action.token));
    setCredentials(action.userId, action.token);
    yield call(getInfoSaga);
  } catch (err) {
    yield put(displaySnackBar(false, err.message || 'Error on set user credentials.'));
  }
}

export function* confirmOTPSaga(action) {
  const { uuid, id, isAIRoute } = action.payload;
  // id - link from dashboard
  if (id) {
    isAIRoute
      ? yield put(push(`/transcript/ai/view/${id}`))
      : yield put(push(`/transcript/view/${id}`));
  }
  // uuid - link from email
  if (uuid) {
    isAIRoute
      ? yield put(push(`/transcripts/ai/${uuid}`))
      : yield put(push(`/transcripts/${uuid}`));
  }
}

export function* requestNewOTPSaga(action) {
  try {
    const { uuid, id } = action.payload;
    const token = yield select(tokenSelector);
    yield call(requestNewOTP, { uuid, id, token });
    yield put(displaySnackBar(true, 'Code sent.'));
  } catch (err) {
    yield put(displaySnackBar(false, 'Error while sending new code.'));
  }
}

export default function*() {
  const getInfoProcess = yield fork(getInfoSaga);
  yield takeEvery(types.LOGIN_WITH_TOKEN, loginWithTokenSaga);
  yield takeEvery(types.SET.USER_TOKEN, setUserTokenSaga);
  // Cancelling event to prevent race condition when a new user...
  // ...wants to log in with token while a previous user still is logged in
  yield takeEvery(types.LOGIN_WITH_TOKEN, () => cancel(getInfoProcess));
  yield takeEvery(types.LOGIN.STARTED, authenticationLoginSaga);
  yield takeEvery(types.LOGOUT, authenticationLogoutSaga);
  yield takeEvery(types.RESET_PASSWORD.REQUEST, resetUserPasswordSaga);
  yield takeEvery(types.RESET_PASSWORD.RESPONSE, resetUserPasswordSnackbarSaga);
  yield takeEvery(
    types.REQUEST_FOR_RESET_PASSWORD_BY_EMAIL.STARTED,
    requestForResetPasswordByEmailSaga,
  );
  yield takeEvery(types.CHANGE_PASSWORD_BY_TOKEN.STARTED, changePasswordByTokenSaga);
  yield takeEvery(types.CONFIRM_OTP, confirmOTPSaga);
  yield takeEvery(types.REQUEST_NEW_OTP, requestNewOTPSaga);
}



// WEBPACK FOOTER //
// ./redux/sagas/authentication.js